import { Link, Outlet, useLocation } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { subscribe } from '@/lib/centrifugo'
import { useStatsStore } from '@/store/stats'
import { useAuthStore } from '@/store/auth'
import { api } from '@/lib/api'

const navItems = [
  { path: '/', label: 'Dashboard' },
  { path: '/tokens', label: 'Tokens' },
  { path: '/grpc', label: 'GRPC' },
  { path: '/rpc', label: 'RPC' },
  { path: '/transactions', label: 'Live TX' },
  { path: '/history', label: 'History' },
]

const MAX_SAMPLES = 100

export default function RootLayout() {
  const { authenticated } = useAuthStore()
  const setStats = useStatsStore((s) => s.setStats)
  const setLatency = useStatsStore((s) => s.setLatency)
  const setSolPrice = useStatsStore((s) => s.setSolPrice)
  const location = useLocation()
  const samplesRef = useRef<number[]>([])
  const allTimeMinRef = useRef<number>(Infinity)
  const allTimeMaxRef = useRef<number>(0)

  useEffect(() => {
    if (!authenticated) return
    api.getStats().then(setStats).catch(() => {})
    let cleanupStats: (() => void) | undefined
    let cleanupTx: (() => void) | undefined
    let cleanupPrice: (() => void) | undefined
    subscribe('live:stats', (data: any) => {
      setStats(data)
    }).then((unsub) => { cleanupStats = unsub })
    subscribe('live:price:solana', (data: any) => {
      if (data?.price != null) setSolPrice(data.price)
    }).then((unsub) => { cleanupPrice = unsub })
    subscribe('live:transactions', (data: any) => {
      const d = data.data || data
      const blockMs = (d.blockUnixTime || 0) * 1000
      if (!blockMs) return
      const delay = Date.now() - blockMs
      if (delay < 0 || delay > 60_000) return
      const samples = samplesRef.current
      samples.push(delay)
      if (samples.length > MAX_SAMPLES) samples.shift()
      if (delay < allTimeMinRef.current) allTimeMinRef.current = delay
      if (delay > allTimeMaxRef.current) allTimeMaxRef.current = delay
      const avg = samples.reduce((a, b) => a + b, 0) / samples.length
      setLatency({ avg, min: allTimeMinRef.current, max: allTimeMaxRef.current })
    }).then((unsub) => { cleanupTx = unsub })
    return () => { cleanupStats?.(); cleanupTx?.(); cleanupPrice?.() }
  }, [authenticated])

  if (!authenticated && location.pathname !== '/login') {
    window.location.href = '/login'
    return null
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <nav style={{
        width: 200,
        background: 'var(--bg-card)',
        borderRight: '1px solid var(--border)',
        padding: '20px 0',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}>
        <div style={{ padding: '0 16px 20px', fontWeight: 700, fontSize: 16, color: 'var(--accent)' }}>
          Proxima
        </div>
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            style={{
              display: 'block',
              padding: '10px 16px',
              color: location.pathname === item.path ? 'var(--accent)' : 'var(--text-dim)',
              background: location.pathname === item.path ? 'var(--bg-hover)' : 'transparent',
              borderLeft: location.pathname === item.path ? '3px solid var(--accent)' : '3px solid transparent',
            }}
          >
            {item.label}
          </Link>
        ))}
      </nav>
      <main style={{ flex: 1, padding: 24, overflow: 'auto' }}>
        <Outlet />
      </main>
    </div>
  )
}
